import React, { useState, useEffect, useRef, useMemo } from "react";
import { X, Paperclip, Loader2, Plus, FileText } from "lucide-react";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import type { Task, TaskStatusDetail, TaskAssignee, TaskAttachment, TaskPriority } from "../../utils/taskHelpers";
import { formatDateForApi } from "../../utils/taskHelpers";
import {
  uploadTaskAttachment,
  deleteTaskAttachment,
  getTaskAttachments,
  uploadTempTaskAttachment,
  getTempTaskAttachments,
  deleteTempTaskAttachment,
  createTask,
  updateTask,
  getTaskStatuses,
} from "../../services/tasks";
import { getTaskProjects } from "../../services/taskProjects";
import type { TaskProject } from "../../types/tasks";
import { getContacts } from "../../services/contacts";
import api from "../../api/axios";

interface AvailableContact {
  id: string;
  name: string;
  type: "person" | "company";
}

interface NewTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (task: any) => void;
  onFetchTaskHistory?: (taskId: number) => Promise<any[]>;
  currentTask?: Task | null;
  /**
   * Colonne del progetto corrente, se fornite dal caller (TaskPageNew le
   * possiede già). In FE-4 il modal carica comunque le colonne del progetto
   * selezionato in autonomia: questa prop resta per i consumer legacy che
   * non passano `initialProjectId` (es. quick-actions widget senza scope).
   * @deprecated in favore di initialProjectId/initialStatusId.
   */
  statusColumns?: TaskStatusDetail[];
  users?: TaskAssignee[];
  sessionKey?: string | null;
  /**
   * @deprecated usare `initialStatusId` (numerico) o `initialProjectId`.
   * Key testuale dello status di default (legacy): viene usata solo quando
   * il modal è aperto senza progetto (vecchia modalità pre-FE-4).
   */
  defaultStatus?: string;
  availableContacts?: AvailableContact[];
  /**
   * Progetto da precompilare all'apertura (FE-4 §6.5).
   * - `null` → inbox (status null)
   * - `number` → progetto specifico, colonne caricate dal BE
   * - `undefined` → modal decide: se currentTask usa il suo progetto, altrimenti inbox.
   * Vincola `initialStatusId` (deve appartenere allo stesso progetto).
   */
  initialProjectId?: number | null;
  /**
   * Colonna da precompilare (solo se initialProjectId è un number).
   * Ignorato in modalità inbox.
   */
  initialStatusId?: number | null;
  /**
   * Lista progetti attivi per la select "Progetto". Se omessa, il modal
   * esegue getTaskProjects() in autonomia (consumer standalone).
   */
  availableProjects?: TaskProject[];
}

// Costanti stabili per evitare re-render infiniti
const EMPTY_STATUS_COLUMNS: TaskStatusDetail[] = [];
const EMPTY_USERS: TaskAssignee[] = [];
const EMPTY_CONTACTS: AvailableContact[] = [];

export default function NewTaskModal({
  isOpen,
  onClose,
  onSubmit,
  onFetchTaskHistory,
  currentTask,
  statusColumns = EMPTY_STATUS_COLUMNS,
  users = EMPTY_USERS,
  sessionKey,
  defaultStatus,
  availableContacts = EMPTY_CONTACTS,
  initialProjectId,
  initialStatusId,
  availableProjects,
}: NewTaskModalProps) {
  const [form, setForm] = useState<Partial<Task>>({
    title: "",
    description: "",
    priority: "MEDIA" as TaskPriority,
    status: undefined,
    assigned_to: [], // Array of users
    due_date: "",
    category: "",
  });

  // --- Progetto/Colonna contestuali (FE-4 §6.5) ---
  // selectedProjectId === null  → inbox (status null)
  // selectedProjectId === number → progetto specifico
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  // selectedStatusId sempre coerente con il progetto selezionato.
  const [selectedStatusId, setSelectedStatusId] = useState<number | null>(null);
  // Colonne del progetto selezionato (caricate dal BE).
  const [projectColumns, setProjectColumns] = useState<TaskStatusDetail[]>([]);
  const [loadingColumns, setLoadingColumns] = useState(false);
  // Progetti disponibili per la select (caricati in autonomia se non passati).
  const [internalProjects, setInternalProjects] = useState<TaskProject[]>([]);
  // Indica che initialProjectId è stato applicato (per non riapplicarlo ad ogni render).
  const lastAppliedInitialRef = useRef<string | null>(null);

  const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [taskHistory, setTaskHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Stati per self-contained
  const [isSaving, setIsSaving] = useState(false);
  const lastAppliedDefaultStatusRef = useRef<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [internalStatusColumns, setInternalStatusColumns] = useState<TaskStatusDetail[]>([]);
  const [internalContacts, setInternalContacts] = useState<AvailableContact[]>([]);

  // Computed: progetti effettivi (props > internal). Usati dalla select Progetto.
  const effectiveProjects = useMemo(() => {
    return availableProjects && availableProjects.length > 0
      ? availableProjects
      : internalProjects;
  }, [availableProjects, internalProjects]);

  // Computed: usa props se disponibili, altrimenti stati interni
  const effectiveStatusColumns = statusColumns?.length ? statusColumns : internalStatusColumns;
  const effectiveContacts = availableContacts?.length ? availableContacts : internalContacts;

  // Assegnati (utenti interni) - integrato con modulo Team
  const [assignedUsers, setAssignedUsers] = useState<number[]>([]);
  const [userSearchInput, setUserSearchInput] = useState("");
  const [teamMembers, setTeamMembers] = useState<any[]>([]);

  // Contatti collegati (contatti CRM)
  const [linkedContacts, setLinkedContacts] = useState<string[]>([]);
  const [contactSearchInput, setContactSearchInput] = useState("");

  // Filter users based on search input
  const filteredUsers = teamMembers.filter(
    (user) =>
      user.name.toLowerCase().includes(userSearchInput.toLowerCase()) &&
      !assignedUsers.includes(user.id)
  );

  // Filter contacts based on search input
  const filteredContacts = effectiveContacts.filter(
    (contact) =>
      contact.name.toLowerCase().includes(contactSearchInput.toLowerCase()) &&
      !linkedContacts.includes(contact.id)
  );

  // Carica team members dalla prop users
  useEffect(() => {
    if (users && users.length > 0) {
      // Mappa users (TaskAssignee) al formato teamMembers
      const mapped = users.map((u) => ({
        id: u.id,
        name: u.username,
        username: u.username,
        email: u.email || "",
        role: "",
        department: "",
      }));
      setTeamMembers(mapped);
    }
  }, [users]);

  // Caricamento autonomo dati (se non passati via props)
  useEffect(() => {
    const loadData = async () => {
      // Carica la lista progetti attivi solo se non passata via props.
      // Necessaria per popolare la select "Progetto" del modal.
      if (!availableProjects || availableProjects.length === 0) {
        try {
          const res = await getTaskProjects({ archived: false });
          if (res.data && Array.isArray(res.data)) {
            setInternalProjects(res.data);
          }
        } catch (error) {
          console.error("Errore caricamento progetti:", error);
        }
      }

      // Carica statusColumns (legacy) solo se:
      //  - non passate via props
      //  - non c'è un initialProjectId (modalità pre-FE-4 usata dal quick-actions widget)
      // In FE-4 la maggior parte dei caller passa initialProjectId e le colonne
      // vengono caricate dall'useEffect dedicato (projectColumns).
      if (
        (!statusColumns || statusColumns.length === 0) &&
        initialProjectId === undefined &&
        !currentTask
      ) {
        try {
          const res = await getTaskStatuses();
          if (res.data && Array.isArray(res.data)) {
            const active = res.data
              .filter((s: TaskStatusDetail) => s && s.is_active && s.key)
              .sort((a: TaskStatusDetail, b: TaskStatusDetail) => (a.order || 0) - (b.order || 0));
            setInternalStatusColumns(active);
          }
        } catch (error) {
          console.error("Errore caricamento stati:", error);
        }
      }

      // Carica contacts solo se non passati via props
      if (!availableContacts || availableContacts.length === 0) {
        try {
          const res = await getContacts();
          if (res.data && Array.isArray(res.data)) {
            const formatted = res.data.map((c: any) => ({
              id: String(c.id),
              name: c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || "N/A",
              type: (c.contact_type === "company" ? "company" : "person") as "person" | "company",
            }));
            setInternalContacts(formatted);
          }
        } catch (error) {
          console.error("Errore caricamento contatti:", error);
        }
      }
    };

    if (isOpen) {
      loadData();
    }
  }, [isOpen, statusColumns, availableContacts, availableProjects, initialProjectId, currentTask]);

  // Carica le colonne del progetto selezionato quando cambia (FE-4 §6.5).
  // Ignorato in modalità inbox (selectedProjectId === null).
  useEffect(() => {
    if (!isOpen) return;
    if (selectedProjectId === null) {
      setProjectColumns([]);
      return;
    }
    let cancelled = false;
    setLoadingColumns(true);
    getTaskStatuses(selectedProjectId)
      .then((res) => {
        if (cancelled) return;
        const data = Array.isArray(res.data) ? res.data : [];
        const active = data
          .filter((s: TaskStatusDetail) => s && s.is_active)
          .sort((a: TaskStatusDetail, b: TaskStatusDetail) => (a.order ?? 0) - (b.order ?? 0));
        setProjectColumns(active);
        // Se l'initialStatusId è compatibile con il nuovo progetto, lo mantengo.
        // Altrimenti seleziono default/prima colonna del nuovo progetto.
        setSelectedStatusId((cur) => {
          if (cur != null && active.some((c) => c.id === cur)) return cur;
          const def = active.find((c) => c.is_default);
          return def ? def.id : active[0]?.id ?? null;
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Errore caricamento colonne progetto:", err);
        setProjectColumns([]);
        setSelectedStatusId(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingColumns(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, selectedProjectId]);

  // Load form data when task changes
  useEffect(() => {
    if (isOpen) {
      if (currentTask) {
        // Editing existing task
        // Extract assigned user IDs from array
        let assignedUserIds: number[] = [];
        if (currentTask.assigned_to && Array.isArray(currentTask.assigned_to)) {
          assignedUserIds = currentTask.assigned_to.map((user) =>
            typeof user === 'number' ? user : user.id
          );
        }

        setForm({
          title: currentTask.title || "",
          description: currentTask.description || "",
          priority: currentTask.priority,
          status: currentTask.status,
          assigned_to: currentTask.assigned_to || [],
          due_date: currentTask.due_date || "",
          category: currentTask.category || "",
        });

        setAssignedUsers(assignedUserIds);

        // Project/status dal task reale (autorevole: usa `project` write field).
        // Per task legacy senza `project` esplicito ma con status.project_id,
        // usiamo quest'ultimo come fallback (mai dedurre dal nome).
        const rawProject = currentTask.project;
        const taskProjectId: number | null =
          typeof rawProject === "number"
            ? rawProject
            : typeof currentTask.status_detail?.project_id === "number"
            ? currentTask.status_detail.project_id
            : typeof currentTask.status?.project_id === "number"
            ? currentTask.status.project_id
            : null;
        const taskStatusId =
          currentTask.status_detail?.id ?? currentTask.status?.id ?? null;
        setSelectedProjectId(taskProjectId);
        setSelectedStatusId(taskStatusId);

        // Load linked contacts if available
        const contacts = (currentTask as any).related_contacts || [];
        setLinkedContacts(contacts.map((c: any) => String(c.id)));

        if (currentTask.id) {
          fetchAttachments(currentTask.id);
        }
      } else {
        // Creating new task
        // Risoluzione del progetto iniziale (FE-4 §6.5):
        //  - initialProjectId esplicito (anche null = inbox)
        //  - altrimenti default inbox (sezione 13 quick-actions: "default inbox se
        //    non riceve un progetto certo")
        const startProjectId =
          initialProjectId !== undefined ? initialProjectId : null;
        const startStatusId =
          startProjectId !== null ? (initialStatusId ?? null) : null;

        setSelectedProjectId(startProjectId);
        // selectedStatusId viene ricalcolato dall'useEffect projectColumns,
        // ma lo impostiamo subito per evitare flicker iniziale.
        setSelectedStatusId(startStatusId);
        lastAppliedInitialRef.current = `${startProjectId}:${startStatusId ?? ""}`;

        // Default status legacy: usato solo se non c'è progetto e la select
        // legacy è ancora attiva (statusColumns passate dal caller).
        const defaultStatusObj = defaultStatus
          ? effectiveStatusColumns.find((s) => s.key === defaultStatus) ||
            effectiveStatusColumns.find((s) => s.is_default)
          : effectiveStatusColumns.find((s) => s.is_default);

        setForm({
          title: "",
          description: "",
          priority: "MEDIA" as TaskPriority,
          status: defaultStatusObj,
          assigned_to: [],
          due_date: "",
          category: "",
        });

        setAssignedUsers([]);
        setLinkedContacts([]);

        if (sessionKey) {
          fetchTempAttachments(sessionKey);
        } else {
          setAttachments([]);
        }
      }
    } else {
      setAttachments([]);
      setAssignedUsers([]);
      setLinkedContacts([]);
      setUserSearchInput("");
      setContactSearchInput("");
      setShowHistory(false);
      setProjectColumns([]);
      lastAppliedInitialRef.current = null;
    }
  }, [isOpen, currentTask, sessionKey, effectiveStatusColumns, defaultStatus, initialProjectId, initialStatusId]);

  // Effetto separato per gestire l'aggiornamento di defaultStatus dopo l'apertura del modal
  useEffect(() => {
    if (isOpen && !currentTask && defaultStatus && effectiveStatusColumns.length > 0) {
      // Evita di riapplicare lo stesso defaultStatus
      if (lastAppliedDefaultStatusRef.current !== defaultStatus) {
        const targetStatus = effectiveStatusColumns.find((s) => s.key === defaultStatus);
        if (targetStatus) {
          setForm((prev) => ({ ...prev, status: targetStatus }));
          lastAppliedDefaultStatusRef.current = defaultStatus;
        }
      }
    }
    // Reset il ref quando il modal si chiude
    if (!isOpen) {
      lastAppliedDefaultStatusRef.current = null;
    }
  }, [isOpen, currentTask, defaultStatus, effectiveStatusColumns]);

  const fetchAttachments = async (taskId: number) => {
    setLoadingAttachments(true);
    try {
      const response = await getTaskAttachments(taskId);
      setAttachments(response.data);
    } catch (error) {
      console.error("Errore caricamento allegati:", error);
    } finally {
      setLoadingAttachments(false);
    }
  };

  const fetchTempAttachments = async (key: string) => {
    setLoadingAttachments(true);
    try {
      const response = await getTempTaskAttachments(key);
      setAttachments(response.data);
    } catch (error) {
      console.error("Errore caricamento allegati temporanei:", error);
    } finally {
      setLoadingAttachments(false);
    }
  };

  const handleChange = (field: keyof typeof form, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      if (currentTask?.id) {
        // Existing task - normal upload
        await uploadTaskAttachment(currentTask.id, file);
        fetchAttachments(currentTask.id);
      } else if (sessionKey) {
        // New task - temporary upload
        await uploadTempTaskAttachment(file, sessionKey);
        fetchTempAttachments(sessionKey);
      }
    } catch (error) {
      console.error("Errore upload file:", error);
      alert("Errore durante l'upload del file");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId: number) => {
    try {
      if (currentTask?.id) {
        await deleteTaskAttachment(attachmentId);
        fetchAttachments(currentTask.id);
      } else if (sessionKey) {
        await deleteTempTaskAttachment(attachmentId);
        fetchTempAttachments(sessionKey);
      }
    } catch (error) {
      console.error("Errore eliminazione allegato:", error);
      alert("Errore durante l'eliminazione dell'allegato");
    }
  };

  const handleDownloadAttachment = async (file: TaskAttachment) => {
    try {
      const response = await api.get(`api/tasks/task-attachments/${file.id}/download/`, {
        responseType: 'blob'
      });

      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Errore download allegato:", error);
      alert("Errore durante il download dell'allegato");
    }
  };

  const handleShowHistory = async () => {
    if (!currentTask?.id || !onFetchTaskHistory) return;

    setLoadingHistory(true);
    try {
      const history = await onFetchTaskHistory(currentTask.id);
      setTaskHistory(history);
      setShowHistory(true);
    } catch (error) {
      console.error("Errore caricamento cronologia:", error);
      alert("Errore durante il caricamento della cronologia");
    } finally {
      setLoadingHistory(false);
    }
  };

  const addAssignedUser = (userId: number) => {
    if (!assignedUsers.includes(userId)) {
      setAssignedUsers((prev) => [...prev, userId]);
      setUserSearchInput("");
    }
  };

  const removeAssignedUser = (userId: number) => {
    setAssignedUsers((prev) => prev.filter((id) => id !== userId));
  };

  const addLinkedContact = (contactId: string) => {
    if (!linkedContacts.includes(contactId)) {
      setLinkedContacts((prev) => [...prev, contactId]);
      setContactSearchInput("");
    }
  };

  const removeLinkedContact = (contactId: string) => {
    setLinkedContacts((prev) => prev.filter((id) => id !== contactId));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSaveInternal();
  };

  const handleSaveInternal = async () => {
    // Validazione base
    if (!form.title?.trim()) {
      setSaveError("Il titolo è obbligatorio");
      return;
    }

    // Validazione progetto/colonna (FE-4 §6.5):
    //  - inbox (selectedProjectId === null) → status deve essere null
    //  - progetto valorizzato → status obbligatorio e coerente
    const isProjectScope = selectedProjectId !== null;
    if (isProjectScope) {
      if (selectedStatusId == null) {
        setSaveError("Seleziona una colonna del progetto");
        return;
      }
    }

    setSaveError(null);
    setIsSaving(true);

    try {
      // Converti priority da stringa a numero
      const PRIORITY_MAP: Record<string, number> = { ALTA: 3, MEDIA: 2, BASSA: 1 };

      // Payload con coppia project/status coerente (FE-4 §6.5):
      //  - inbox → entrambi null
      //  - progetto → entrambi valorizzati
      // Il backend accetta PATCH atomico con project+status (mock lo supporta).
      // Per edit con cambio progetto, il PATCH single-shot è preferito a due
      // chiamate separate (move + patch metadata): gestisce rollback in un unico
      // step e mantiene la response autoritativa.
      const taskData: any = {
        title: form.title,
        description: form.description || "",
        // Coerenza project/status: entrambi null per inbox, entrambi valorizzati per progetto.
        project: isProjectScope ? selectedProjectId : null,
        status: isProjectScope ? selectedStatusId : null,
        priority: PRIORITY_MAP[form.priority || "MEDIA"] || 2,
        due_date: form.due_date ? formatDateForApi(form.due_date) : null,
        category: form.category || "",
      };

      // Handle assigned users (always send, even if empty, to allow removal)
      taskData.assigned_to_ids = assignedUsers;

      // Handle related contacts (always send, even if empty, to allow removal)
      taskData.related_contact_ids = linkedContacts.map(id => parseInt(id, 10));

      // If new task with temp attachments, include session key
      if (!currentTask?.id && sessionKey) {
        taskData.temp_attachments = sessionKey;
      }

      let response;
      if (currentTask?.id) {
        // Update existing task
        response = await updateTask(currentTask.id, taskData);
      } else {
        // Create new task
        response = await createTask(taskData);
      }

      // Notifica il parent (opzionale)
      if (onSubmit) {
        onSubmit(response.data);
      }

      onClose();
    } catch (error: any) {
      console.error("Errore salvataggio task:", error);

      const errorData = error?.response?.data;
      let message = "Errore durante il salvataggio. Riprova.";

      if (typeof errorData === "string") {
        message = errorData;
      } else if (errorData?.detail) {
        message = errorData.detail;
      } else if (errorData?.code === "status_project_mismatch") {
        message = "La colonna selezionata non appartiene al progetto.";
      } else if (errorData?.code === "project_archived") {
        message = "Il progetto è archiviato.";
      } else if (errorData?.code === "status_inactive") {
        message = "La colonna non è più utilizzabile.";
      } else if (errorData?.assigned_to || errorData?.assigned_to_ids) {
        message = errorData.assigned_to?.[0] || errorData.assigned_to_ids?.[0] ||
          "Non puoi assegnare il task a utenti di un tenant diverso.";
      } else if (typeof errorData === "object" && errorData !== null) {
        const firstKey = Object.keys(errorData)[0];
        if (firstKey) {
          const val = errorData[firstKey];
          message = `${firstKey}: ${Array.isArray(val) ? val[0] : val}`;
        }
      }

      setSaveError(message);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  if (showHistory) {
    return (
      <div className="flex flex-col h-full bg-[var(--hexa-surface-elevated)]">
        <div className="flex-shrink-0 border-b border-[var(--hexa-border)] px-4 py-3">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold !text-[var(--hexa-text-primary)]">Cronologia Task: {currentTask?.title}</h2>
            <button
              type="button"
              onClick={() => setShowHistory(false)}
              className="h-6 w-6 rounded-lg grid place-items-center text-[var(--hexa-text-muted)] hover:text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-topbar-icon-hover)] transition"
              aria-label="Chiudi"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2.5 scrollbar-hexa">
          {taskHistory.length === 0 ? (
            <p className="text-[var(--hexa-text-secondary)] text-center py-8">Nessuna cronologia disponibile</p>
          ) : (
            taskHistory.map((entry, idx) => (
              <div key={idx} className="rounded-lg bg-[var(--hexa-button-cancel)] border-[var(--hexa-border)] p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="text-xs text-[var(--hexa-text-primary)] font-medium">{entry.change_type}</div>
                    <div className="text-[10px] text-[var(--hexa-text-secondary)] mt-1">
                      {entry.changed_by} • {new Date(entry.changed_at).toLocaleString("it-IT")}
                    </div>
                    {entry.old_value && entry.new_value && (
                      <div className="mt-2 text-[10px]">
                        <span className="text-red-400">- {entry.old_value}</span>
                        <br />
                        <span className="text-green-400">+ {entry.new_value}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full max-h-full bg-[var(--hexa-modal-bg)] overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 border-b border-[var(--hexa-border)] px-4 py-3">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold !text-[var(--hexa-text-primary)]">
              {currentTask ? "Modifica Task" : "Nuovo Task"}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="h-6 w-6 rounded-lg grid place-items-center text-[var(--hexa-text-muted)] hover:text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-topbar-icon-hover)] transition"
              aria-label="Chiudi"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
        {/* Form Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-hexa">
          {/* Titolo */}
          <div>
            <label className="block text-xs font-medium text-[var(--hexa-text-primary)] mb-1">
              Titolo <span className="text-[#ff6467]">*</span>
            </label>
            <input
              type="text"
              placeholder="Es. Preparare dichiarazione IVA Q1 2025"
              value={form.title || ""}
              onChange={(e) => handleChange("title", e.target.value)}
              className="w-full rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] text-[var(--hexa-text-primary)] placeholder-[var(--hexa-text-muted)] px-3 py-2 text-xs focus:outline-none focus:border-[var(--hexa-border)] transition"
              required
            />
          </div>

          {/* Descrizione */}
          <div>
            <label className="block text-xs font-medium text-[var(--hexa-text-primary)] mb-1">Descrizione</label>
            <textarea
              placeholder="Dettagli / dettagli del task..."
              value={form.description || ""}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={3}
              className="w-full rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] text-[var(--hexa-text-primary)] placeholder-[var(--hexa-text-muted)] px-3 py-2 text-xs focus:outline-none focus:border-[var(--hexa-border)] transition resize-y min-h-[60px] max-h-[150px]"
            />
          </div>

          {/* Progetto + Colonna (FE-4 §6.5) */}
          <div className="space-y-2">
            <div>
              <label className="block text-xs font-medium text-[var(--hexa-text-primary)] mb-1">
                Progetto
              </label>
              <select
                value={selectedProjectId === null ? "__inbox__" : String(selectedProjectId)}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "__inbox__") {
                    setSelectedProjectId(null);
                    setSelectedStatusId(null);
                  } else {
                    const pid = Number(v);
                    setSelectedProjectId(Number.isFinite(pid) ? pid : null);
                    // selectedStatusId viene ricalcolato dall'useEffect projectColumns.
                    setSelectedStatusId(null);
                  }
                }}
                className="w-full rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] text-[var(--hexa-text-primary)] px-3 py-2 text-xs focus:outline-none focus:border-[var(--hexa-border)] transition"
                aria-label="Progetto"
              >
                <option value="__inbox__">Senza progetto (inbox)</option>
                {effectiveProjects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Colonna: visibile solo se è selezionato un progetto.
                In inbox non esiste colonna (status null). */}
            {selectedProjectId !== null && (
              <div>
                <label className="block text-xs font-medium text-[var(--hexa-text-primary)] mb-1">
                  Colonna <span className="text-[#ff6467]">*</span>
                </label>
                <select
                  value={selectedStatusId ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedStatusId(v === "" ? null : Number(v));
                  }}
                  disabled={loadingColumns || projectColumns.length === 0}
                  className="w-full rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] text-[var(--hexa-text-primary)] px-3 py-2 text-xs focus:outline-none focus:border-[var(--hexa-border)] transition disabled:opacity-60 disabled:cursor-not-allowed"
                  aria-label="Colonna"
                  required
                >
                  {loadingColumns && (
                    <option value="">Caricamento colonne...</option>
                  )}
                  {!loadingColumns && projectColumns.length === 0 && (
                    <option value="">Nessuna colonna disponibile</option>
                  )}
                  {!loadingColumns &&
                    projectColumns.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                </select>
              </div>
            )}

            {/* Priorità: ora in riga singola sotto progetto/colonna */}
            <div>
              <label className="block text-xs font-medium text-[var(--hexa-text-primary)] mb-1">
                Priorità <span className="text-[#ff6467]">*</span>
              </label>
              <select
                value={form.priority}
                onChange={(e) => handleChange("priority", e.target.value as TaskPriority)}
                className="w-full rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] text-[var(--hexa-text-primary)] px-3 py-2 text-xs focus:outline-none focus:border-[var(--hexa-border)] transition"
                required
              >
                <option value="BASSA">Bassa</option>
                <option value="MEDIA">Media</option>
                <option value="ALTA">Alta</option>
              </select>
            </div>
          </div>

          {/* Assegnato a */}
          <div>
            <label className="block text-xs font-medium text-[var(--hexa-text-primary)] mb-1">
              Assegnato a
            </label>

            {/* Container tipo input con chips inside */}
            <div className="relative">
              <div className="w-full min-h-[34px] rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] px-2.5 py-1.5 flex flex-wrap items-center gap-1.5 focus-within:border-[var(--hexa-border)] transition">
                {/* Badge utenti già assegnati */}
                {assignedUsers.map((userId) => {
                  const user = teamMembers.find((u) => u.id === userId);
                  return (
                    <Badge
                      key={userId}
                      className="bg-[#2b7fff] hover:bg-[#2167e6] text-white cursor-pointer text-[10px] shrink-0"
                      onClick={() => removeAssignedUser(userId)}
                    >
                      {user?.name || `User ${userId}`}
                      <X className="w-2.5 h-2.5 ml-0.5" />
                    </Badge>
                  );
                })}

                {/* Input di ricerca senza bordo */}
                <input
                  type="text"
                  placeholder={assignedUsers.length === 0 ? "Cerca membri del team..." : ""}
                  value={userSearchInput}
                  onChange={(e) => setUserSearchInput(e.target.value)}
                  className="flex-1 min-w-[90px] bg-transparent border-none text-[var(--hexa-text-primary)] placeholder-[var(--hexa-text-muted)] text-xs focus:outline-none"
                />
              </div>

              {/* Dropdown con utenti filtrati */}
              {userSearchInput && filteredUsers.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-[var(--hexa-modal-bg)] border border-[var(--hexa-border)] rounded-lg shadow-xl max-h-32 overflow-y-auto scrollbar-hexa">
                  {filteredUsers.map((user) => (
                    <div
                      key={user.id}
                      onClick={() => addAssignedUser(user.id)}
                      className="px-2.5 py-1.5 hover:bg-[var(--hexa-button-cancel)] cursor-pointer text-[var(--hexa-text-primary)] text-xs flex items-center justify-between"
                    >
                      <div>
                        <div className="font-medium">{user.name}</div>
                        <div className="text-[10px] text-[var(--hexa-text-secondary)]">
                          {user.role} • {user.department}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Contatti collegati */}
          <div>
            <label className="block text-xs font-medium text-[var(--hexa-text-primary)] mb-1">Contatti collegati</label>

            {/* Container tipo input con chips inside */}
            <div className="relative">
              <div className="w-full min-h-[34px] rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] px-2.5 py-1.5 flex flex-wrap items-center gap-1.5 focus-within:border-[var(--hexa-border)] transition">
                {/* Badge contatti selezionati */}
                {linkedContacts.map((contactId) => {
                  const contact = effectiveContacts.find((c) => c.id === contactId);
                  return (
                    <Badge
                      key={contactId}
                      className="bg-[var(--hexa-purple)] hover:bg-[var(--hexa-purple-hover)] text-white cursor-pointer text-[10px] shrink-0"
                      onClick={() => removeLinkedContact(contactId)}
                    >
                      {contact?.name}
                      <X className="w-2.5 h-2.5 ml-0.5" />
                    </Badge>
                  );
                })}

                {/* Input di ricerca senza bordo */}
                <input
                  type="text"
                  placeholder={linkedContacts.length === 0 ? "Cerca contatti..." : ""}
                  value={contactSearchInput}
                  onChange={(e) => setContactSearchInput(e.target.value)}
                  className="flex-1 min-w-[90px] bg-transparent border-none text-[var(--hexa-text-primary)] placeholder-[var(--hexa-text-muted)] text-xs focus:outline-none"
                />
              </div>

              {/* Dropdown con contatti filtrati */}
              {contactSearchInput && filteredContacts.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-[var(--hexa-modal-bg)] border border-[var(--hexa-border)] rounded-lg shadow-xl max-h-32 overflow-y-auto scrollbar-hexa">
                  {filteredContacts.map((contact) => (
                    <div
                      key={contact.id}
                      onClick={() => addLinkedContact(contact.id)}
                      className="px-2.5 py-1.5 hover:bg-[var(--hexa-button-cancel)] cursor-pointer text-[var(--hexa-text-primary)] text-xs flex items-center justify-between"
                    >
                      <span>{contact.name}</span>
                      <Badge variant="secondary" className="bg-[var(--hexa-border)] text-[var(--hexa-text-secondary)] text-[10px]">
                        {contact.type === "person" ? "Persona" : "Azienda"}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Data di scadenza e Categoria */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[var(--hexa-text-primary)] mb-1">Data di scadenza</label>
              <Input
                type="date"
                value={form.due_date || ""}
                onChange={(e) => handleChange("due_date", e.target.value)}
                className="w-full h-auto rounded-lg px-3 py-2 text-xs bg-[var(--hexa-input-bg)] border-[var(--hexa-input-border)] text-[var(--hexa-text-primary)]"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-[var(--hexa-text-primary)] mb-1">Categoria</label>
              <input
                type="text"
                placeholder="Es. Fiscale, Admin, etc."
                value={form.category || ""}
                onChange={(e) => handleChange("category", e.target.value)}
                className="w-full rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] text-[var(--hexa-text-primary)] placeholder-[var(--hexa-text-muted)] px-3 py-2 text-xs focus:outline-none focus:border-[var(--hexa-border)] transition"
              />
            </div>
          </div>

          {/* Allegati */}
          <div>
            {/* Header con label e pulsante + */}
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-[var(--hexa-text-primary)] flex items-center gap-2">
                <Paperclip className="h-3.5 w-3.5" />
                <span>Allegati</span>
              </label>
              <label
                className="w-7 h-7 rounded-md bg-[var(--hexa-surface-elevated)] border border-[var(--hexa-border)] flex items-center justify-center cursor-pointer hover:bg-[var(--hexa-surface-hover)] transition"
              >
                <Plus className="h-3.5 w-3.5 text-[var(--hexa-purple)]" />
                <input
                  type="file"
                  onChange={(e) => e.target.files && handleUpload(e.target.files[0])}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.gif,.webp,.zip,.rar"
                  disabled={uploading}
                />
              </label>
            </div>

            {/* File chips */}
            {loadingAttachments ? (
              <p className="text-[var(--hexa-text-secondary)] text-xs mt-2">Caricamento allegati...</p>
            ) : attachments.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {attachments.map((file) => (
                  <div
                    key={file.id}
                    className="inline-flex items-center gap-1.5 py-1 px-2.5 rounded-md bg-[var(--hexa-surface-elevated)] border border-[var(--hexa-border)] max-w-full"
                  >
                    <FileText className="h-3 w-3 text-[var(--hexa-purple)] opacity-60 flex-shrink-0" />
                    <span
                      onClick={() => handleDownloadAttachment(file)}
                      className="text-xs text-[var(--hexa-text-primary)] truncate hover:underline cursor-pointer"
                    >
                      {file.filename}
                    </span>
                    {file.file_size && (
                      <span className="text-[11px] text-[var(--hexa-text-muted)] flex-shrink-0">
                        {Math.round(file.file_size / 1024)} KB
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDeleteAttachment(file.id)}
                      className="text-[var(--hexa-text-muted)] hover:text-[var(--hexa-text-primary)] transition flex-shrink-0"
                      aria-label="Elimina"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-[var(--hexa-border)] px-4 py-3">
          {/* Errore inline */}
          {saveError && (
            <div className="mb-2 p-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs">
              {saveError}
            </div>
          )}

          <div className="flex gap-2.5">
            {/* Annulla Mini-Card */}
            <div className="flex-1 bg-[var(--hexa-button-cancel)] rounded-lg p-2.5 flex items-center justify-center border border-[var(--hexa-border)]">
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="w-full text-[var(--hexa-button-cancel-text)] hover:text-[var(--hexa-text-primary)] disabled:opacity-50"
              >
                Annulla
              </button>
            </div>

            {/* Salva Mini-Card */}
            <div className="flex-1 rounded-lg flex items-center justify-center">
              <button
                type="submit"
                disabled={isSaving}
                className="w-full h-full bg-[var(--hexa-purple)] hover:bg-[var(--hexa-purple-hover)] text-white rounded-lg py-2.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {isSaving ? "Salvataggio..." : "Salva"}
              </button>
            </div>
          </div>
        </div>
        </form>
      </div>
  );
}
